/**
 * 공지사항 Hooks (V86)
 *
 * 공지사항 CRUD 및 첨부파일 업로드 관련 hooks
 *
 * V86 성능 개선:
 * - 목록/권한 데이터 세션 내 캐싱
 * - 중복 API 호출 방지
 * - useEffect dependency 최적화
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebase/config';

// ============================================
// V86: 세션 내 캐시 (메모리)
// ============================================

/** V70G8E: 캐시 소유 계정 (로그아웃·계정 전환 시 다른 계정의 읽음 상태가 섞이지 않도록) */
function currentUid(): string | null {
  try {
    return auth?.currentUser?.uid ?? null;
  } catch {
    return null;
  }
}

/** 목록 캐시 - V5.15: 페이지네이션 지원, V64: boardType별 캐시 */
type ListCacheEntry = {
  /** V70G8E: 이 캐시를 만든 계정 */
  uid: string | null;
  announcements: AnnouncementListItem[];
  pinnedAnnouncements?: AnnouncementListItem[];
  regularAnnouncements?: AnnouncementListItem[];
  currentPage?: number;
  totalPages?: number;
  totalRegularCount?: number;
  hasNext: boolean;
  lastDocId?: string;
  timestamp: number;
};
const listCacheByBoard: Map<AnnouncementBoardType, ListCacheEntry> = new Map();

/** 권한 캐시 */
let permissionCache: {
  canWrite: boolean;
  timestamp: number;
} | null = null;

/** 캐시 유효 시간 (5분) */
const CACHE_TTL = 5 * 60 * 1000;

// ============================================
// 타입 정의
// ============================================

/** 첨부파일 정보 */
export interface AnnouncementAttachment {
  id: string;
  name: string;
  url: string;
  size: number;
  contentType: string;
  storagePath: string;
  uploadedAt: string;
}

/** V64: 공지사항 게시판 분류 */
export type AnnouncementBoardType = 'notice' | 'familyEvents';

/** V64: 게시판 라벨 (FE/BE 공유) */
export const ANNOUNCEMENT_BOARD_LABELS: Record<AnnouncementBoardType, string> = {
  notice: '공지사항',
  familyEvents: '경조사 안내',
};

/** V64: 기본 게시판 */
export const DEFAULT_ANNOUNCEMENT_BOARD_TYPE: AnnouncementBoardType = 'notice';

/** 공지사항 목록 아이템 */
export interface AnnouncementListItem {
  id: string;
  title: string;
  isPinned: boolean;
  visibilityRoleIds: string[];
  attachmentCount: number;
  companyCode?: 'KR' | 'CN' | 'ALL'; // V60: 국가 스코프
  boardType?: AnnouncementBoardType; // V64: 게시판 분류 (fallback 'notice')
  createdAt: string;
  createdBy: string;
  createdByName: string;
  /** V70G8C: 현재 사용자 기준 미확인 (LNB 배지와 같은 서버 규칙) */
  isUnread?: boolean;
}

/** V279: 본문 포맷 — 'html'은 리치 에디터, 'plain'은 평문(legacy) */
export type AnnouncementContentFormat = 'html' | 'plain';

/** 공지사항 상세 */
export interface AnnouncementDetail {
  id: string;
  title: string;
  content: string;
  /** V279: 본문 포맷. 누락 시 'plain'으로 간주(legacy) */
  contentFormat?: AnnouncementContentFormat;
  visibilityRoleIds: string[];
  isPinned: boolean;
  attachments: AnnouncementAttachment[];
  companyCode?: 'KR' | 'CN' | 'ALL'; // V60: 국가 스코프
  boardType?: AnnouncementBoardType; // V64: 게시판 분류 (fallback 'notice')
  /** V66: 노출기간 (null=제한없음). 기존 공지는 미존재 */
  visibilityDurationDays?: number | null;
  /** V66: 자동 미노출 시각 ISO. 기존 공지/제한없음 공지는 미존재 */
  autoHideAt?: string | null;
  /** V66: 자동 미노출 처리 여부 */
  hiddenByScheduler?: boolean;
  /** V70G7B: 팝업 설정 */
  isPopup?: boolean;
  popupStartAt?: string | null;
  popupEndAt?: string | null;
  popupDismissPolicy?: PopupDismissPolicy;
  popupPriority?: number;
  popupRevision?: number;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
}

/** V70G7B: 팝업 닫기 정책 */
export type PopupDismissPolicy = 'once' | 'onceDismissed' | 'hideForDay' | 'session' | 'daily';

export const POPUP_DISMISS_POLICY_OPTIONS: { value: PopupDismissPolicy; label: string; description: string }[] = [
  { value: 'onceDismissed', label: '한 번 닫으면 숨김', description: '사용자가 닫기를 누를 때까지 접속 시마다 표시합니다.' },
  { value: 'once', label: '최초 1회 표시', description: '처음 표시된 이후에는 닫지 않아도 다시 표시하지 않습니다.' },
  { value: 'hideForDay', label: '오늘 하루 보지 않기', description: '"오늘 하루 보지 않기"를 누르면 KST 자정까지 숨깁니다.' },
  { value: 'session', label: '세션 동안 숨기기', description: '닫으면 현재 브라우저 탭 세션 동안 숨기고, 새 세션에서 다시 표시합니다.' },
  { value: 'daily', label: '매일 최초 접속 시 표시', description: '하루(KST)에 한 번만 표시합니다.' },
];

export const DEFAULT_POPUP_PRIORITY = 100;

/** V70G7B: 팝업 설정 요청 필드 (시각은 ms epoch) */
export interface PopupSettingsRequest {
  isPopup?: boolean;
  popupStartAt?: number | null;
  popupEndAt?: number | null;
  popupDismissPolicy?: PopupDismissPolicy;
  popupPriority?: number;
}

/** V70G8B: 팝업 소스 — 일반 공지 / 게임대회 대시보드 공지 */
export type PopupSource = 'erp' | 'gameCenter';

/** V70G7B: 활성 팝업 항목 */
export interface ActivePopupAnnouncement {
  /** V70G8B: 미지정 = 'erp' */
  source?: PopupSource;
  id: string;
  title: string;
  content: string;
  contentFormat: AnnouncementContentFormat;
  attachments: AnnouncementAttachment[];
  companyCode: 'KR' | 'CN' | 'ALL';
  popupPriority: number;
  popupRevision: number;
  popupDismissPolicy: PopupDismissPolicy;
  popupStartAt: string | null;
  popupEndAt: string | null;
  /** V70G7D: min(popupEndAt, autoHideAt). 지나면 화면에서 제거 */
  displayUntil?: string | null;
  createdAt: string;
}

/** V66: 노출기간 옵션 (FE 폼용) */
export type AnnouncementVisibilityDurationDays = 1 | 7 | 14 | 30;

/** V66: 노출기간 옵션 메타 */
export const VISIBILITY_DURATION_OPTIONS: { value: AnnouncementVisibilityDurationDays | null; label: string }[] = [
  { value: null, label: '제한 없음' },
  { value: 1, label: '1일' },
  { value: 7, label: '1주 (7일)' },
  { value: 14, label: '2주 (14일)' },
  { value: 30, label: '30일' },
];

/** 공지사항 생성 요청 */
export interface CreateAnnouncementRequest extends PopupSettingsRequest {
  title: string;
  content: string;
  /** V279: 본문 포맷 */
  contentFormat?: AnnouncementContentFormat;
  visibilityRoleIds: string[];
  isPinned?: boolean;
  companyCode?: 'KR' | 'CN' | 'ALL'; // V60: system_admin용 국가 스코프 선택
  boardType?: AnnouncementBoardType; // V64: 게시판 분류
  /** V66: 노출기간 (null=제한없음). 미지정 시 BE는 null로 처리 */
  visibilityDurationDays?: AnnouncementVisibilityDurationDays | null;
  attachments?: Omit<AnnouncementAttachment, 'uploadedAt'>[];
  notifyOnPublish?: boolean; // V63 Batch6: 알림 발송 여부
}

/** 공지사항 수정 요청 */
export interface UpdateAnnouncementRequest extends PopupSettingsRequest {
  id: string;
  /** V70G7B: true 면 popupRevision +1 → 기존 닫기 기록 무효화(재노출) */
  popupBumpRevision?: boolean;
  title?: string;
  content?: string;
  /** V279: 본문 포맷 */
  contentFormat?: AnnouncementContentFormat;
  visibilityRoleIds?: string[];
  isPinned?: boolean;
  boardType?: AnnouncementBoardType; // V64: 게시판 분류 변경
  /**
   * V66: 노출기간 변경.
   * - 미지정(undefined): 변경 안 함
   * - null/0: 제한없음으로 전환
   * - 1/7/14/30: 새 만료일로 갱신
   */
  visibilityDurationDays?: AnnouncementVisibilityDurationDays | null;
  attachments?: AnnouncementAttachment[];
}

// 응답 타입
/**
 * V5.15: 중요공지/일반공지 분리 + 페이지네이션
 */
interface GetAnnouncementsListResponse {
  success: boolean;
  /** @deprecated 하위 호환성용 */
  announcements: AnnouncementListItem[];
  /** V5.15: 중요공지 목록 */
  pinnedAnnouncements?: AnnouncementListItem[];
  /** V5.15: 일반공지 목록 (현재 페이지) */
  regularAnnouncements?: AnnouncementListItem[];
  /** V5.15: 일반공지 총 개수 */
  totalRegularCount?: number;
  /** V5.15: 총 페이지 수 */
  totalPages?: number;
  /** V5.15: 현재 페이지 */
  currentPage?: number;
  hasNext: boolean;
  lastDocId?: string;
}

interface GetAnnouncementDetailResponse {
  success: boolean;
  announcement: AnnouncementDetail;
  canEdit?: boolean; // V60: 이 공지에 대한 수정/삭제 권한
}

interface CreateAnnouncementResponse {
  success: boolean;
  id: string;
  message: string;
}

interface UpdateAnnouncementResponse {
  success: boolean;
  message: string;
}

interface DeleteAnnouncementResponse {
  success: boolean;
  message: string;
}

interface PrepareUploadResponse {
  success: boolean;
  fileId: string;
  uploadUrl: string;
  storagePath: string;
  expiresIn: number;
}

interface ConfirmUploadResponse {
  success: boolean;
  downloadUrl: string;
  attachment: AnnouncementAttachment;
}

/** V267: Base64 직접 업로드 응답 */
interface UploadFileResponse {
  success: boolean;
  downloadUrl: string;
  attachment: AnnouncementAttachment;
}

interface CheckPermissionResponse {
  canWrite: boolean;
}

// ============================================
// 공지사항 목록 조회 Hook (V5.15: 페이지네이션)
// ============================================

/**
 * V5.15: 페이지네이션 지원 결과 타입
 */
export interface UseAnnouncementsListResult {
  /** @deprecated 하위 호환성용 - pinnedAnnouncements + regularAnnouncements 사용 권장 */
  announcements: AnnouncementListItem[];
  /** V5.15: 중요공지 목록 (isPinned=true, 항상 전체) */
  pinnedAnnouncements: AnnouncementListItem[];
  /** V5.15: 일반공지 목록 (isPinned=false, 현재 페이지) */
  regularAnnouncements: AnnouncementListItem[];
  isLoading: boolean;
  error: string | null;
  /** V5.15: 다음 페이지 존재 여부 */
  hasNext: boolean;
  /** V5.15: 현재 페이지 번호 */
  currentPage: number;
  /** V5.15: 총 페이지 수 */
  totalPages: number;
  /** V5.15: 일반공지 총 개수 */
  totalRegularCount: number;
  /** V5.15: 페이지 변경 */
  goToPage: (page: number) => Promise<void>;
  /** @deprecated V5.15: goToPage 사용 권장 */
  fetchMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * V5.15: 공지사항 목록 조회 Hook (페이지네이션 지원)
 *
 * - 중요공지: 항상 전체 조회 (페이지네이션 미적용)
 * - 일반공지: 10개 단위 페이지네이션
 */
export function useAnnouncementsList(
  boardType: AnnouncementBoardType = DEFAULT_ANNOUNCEMENT_BOARD_TYPE
): UseAnnouncementsListResult {
  // V5.15: 중요/일반 분리 상태
  const [pinnedAnnouncements, setPinnedAnnouncements] = useState<AnnouncementListItem[]>([]);
  const [regularAnnouncements, setRegularAnnouncements] = useState<AnnouncementListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRegularCount, setTotalRegularCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(false);

  // V86: 중복 호출 방지
  const isFetchingRef = useRef(false);

  const fetchList = useCallback(async (page: number, forceRefresh = false) => {
    if (!functions) {
      setError('Firebase가 초기화되지 않았습니다.');
      return;
    }

    // 중복 호출 방지
    if (isFetchingRef.current) return;

    // V64: boardType별 캐시 체크 (1페이지, 새로고침 아닐 때만)
    const cached = listCacheByBoard.get(boardType);
    if (
      page === 1 && !forceRefresh && cached
      && cached.uid === currentUid() // V70G8E: 다른 계정 캐시는 사용하지 않음
      && Date.now() - cached.timestamp < CACHE_TTL
    ) {
      setPinnedAnnouncements(cached.pinnedAnnouncements || []);
      setRegularAnnouncements(cached.regularAnnouncements || []);
      setCurrentPage(cached.currentPage || 1);
      setTotalPages(cached.totalPages || 1);
      setTotalRegularCount(cached.totalRegularCount || 0);
      setHasNext(cached.hasNext);
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const getList = httpsCallable<
        { pageSize?: number; page?: number; boardType?: AnnouncementBoardType },
        GetAnnouncementsListResponse
      >(functions, 'getAnnouncementsList');

      const result = await getList({
        pageSize: 10, // V5.15: 일반공지 10개씩
        page,
        boardType, // V64
      });

      if (result.data.success) {
        const pinned = result.data.pinnedAnnouncements || [];
        const regular = result.data.regularAnnouncements || [];
        const pages = result.data.totalPages || 1;
        const count = result.data.totalRegularCount || 0;
        const currPage = result.data.currentPage || 1;
        const next = result.data.hasNext;

        setPinnedAnnouncements(pinned);
        setRegularAnnouncements(regular);
        setCurrentPage(currPage);
        setTotalPages(pages);
        setTotalRegularCount(count);
        setHasNext(next);

        // V64: 캐시 업데이트 (boardType별, 1페이지만)
        if (page === 1) {
          listCacheByBoard.set(boardType, {
            uid: currentUid(),
            announcements: [...pinned, ...regular],
            pinnedAnnouncements: pinned,
            regularAnnouncements: regular,
            currentPage: currPage,
            totalPages: pages,
            totalRegularCount: count,
            hasNext: next,
            timestamp: Date.now(),
          });
        }
      } else {
        throw new Error('목록 조회 실패');
      }
    } catch (err: any) {
      console.error('[useAnnouncementsList] Error:', err);
      setError(err.message || '공지사항 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [boardType]);

  // V64: boardType 변경 시 자동 재조회 (초기 로드 포함)
  useEffect(() => {
    fetchList(1, false);
  }, [fetchList]);

  // V70G8E: 공지 상세에서 읽음 저장이 성공하면 열려 있는 목록의 해당 공지만 즉시 읽음 표시 (재조회 없음)
  useEffect(() => subscribeAnnouncementRead((id) => {
    setPinnedAnnouncements((prev) => markItemRead(prev, id));
    setRegularAnnouncements((prev) => markItemRead(prev, id));
  }), []);

  // V5.15: 페이지 이동
  const goToPage = useCallback(async (page: number) => {
    if (page < 1 || page > totalPages || isLoading || isFetchingRef.current) return;
    await fetchList(page, true);
  }, [totalPages, isLoading, fetchList]);

  // @deprecated: 하위 호환성
  const fetchMore = useCallback(async () => {
    if (!hasNext || isLoading || isFetchingRef.current) return;
    await fetchList(currentPage + 1, true);
  }, [hasNext, isLoading, currentPage, fetchList]);

  // 강제 새로고침
  const refetch = useCallback(async () => {
    listCacheByBoard.delete(boardType);
    await fetchList(1, true);
  }, [fetchList, boardType]);

  // 하위 호환성: announcements = pinned + regular
  const announcements = [...pinnedAnnouncements, ...regularAnnouncements];

  return {
    announcements,
    pinnedAnnouncements,
    regularAnnouncements,
    isLoading,
    error,
    hasNext,
    currentPage,
    totalPages,
    totalRegularCount,
    goToPage,
    fetchMore,
    refetch,
  };
}

// ============================================
// 공지사항 상세 조회 Hook
// ============================================

export interface UseAnnouncementDetailResult {
  announcement: AnnouncementDetail | null;
  canEdit: boolean; // V60: 이 공지에 대한 수정/삭제 권한
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAnnouncementDetail(id: string | null): UseAnnouncementDetailResult {
  const [announcement, setAnnouncement] = useState<AnnouncementDetail | null>(null);
  const [canEdit, setCanEdit] = useState(false); // V60
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!id || !functions) {
      setAnnouncement(null);
      setCanEdit(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getDetail = httpsCallable<{ id: string }, GetAnnouncementDetailResponse>(
        functions,
        'getAnnouncementDetail'
      );

      const result = await getDetail({ id });

      if (result.data.success) {
        setAnnouncement(result.data.announcement);
        setCanEdit(result.data.canEdit || false); // V60
      } else {
        throw new Error('상세 조회 실패');
      }
    } catch (err: any) {
      console.error('[useAnnouncementDetail] Error:', err);
      setError(err.message || '공지사항을 불러오는 중 오류가 발생했습니다.');
      setAnnouncement(null);
      setCanEdit(false);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    announcement,
    canEdit, // V60
    isLoading,
    error,
    refetch: fetchDetail,
  };
}

// ============================================
// 공지사항 CRUD Mutations Hook
// ============================================

export interface UseAnnouncementMutationsResult {
  createAnnouncement: (data: CreateAnnouncementRequest) => Promise<string>;
  updateAnnouncement: (data: UpdateAnnouncementRequest) => Promise<void>;
  deleteAnnouncement: (id: string) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
}

export function useAnnouncementMutations(): UseAnnouncementMutationsResult {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createAnnouncement = async (data: CreateAnnouncementRequest): Promise<string> => {
    if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);

    try {
      const create = httpsCallable<CreateAnnouncementRequest, CreateAnnouncementResponse>(
        functions,
        'createAnnouncement'
      );

      const result = await create(data);

      if (result.data.success) {
        return result.data.id;
      } else {
        throw new Error('공지사항 등록 실패');
      }
    } catch (err: any) {
      console.error('[createAnnouncement] Error:', err);
      const message = err.message || '공지사항 등록 중 오류가 발생했습니다.';
      setError(message);
      throw new Error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateAnnouncement = async (data: UpdateAnnouncementRequest): Promise<void> => {
    if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);

    try {
      const update = httpsCallable<UpdateAnnouncementRequest, UpdateAnnouncementResponse>(
        functions,
        'updateAnnouncement'
      );

      const result = await update(data);

      if (!result.data.success) {
        throw new Error('공지사항 수정 실패');
      }
    } catch (err: any) {
      console.error('[updateAnnouncement] Error:', err);
      const message = err.message || '공지사항 수정 중 오류가 발생했습니다.';
      setError(message);
      throw new Error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteAnnouncement = async (id: string): Promise<void> => {
    if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);

    try {
      const remove = httpsCallable<{ id: string }, DeleteAnnouncementResponse>(
        functions,
        'deleteAnnouncement'
      );

      const result = await remove({ id });

      if (!result.data.success) {
        throw new Error('공지사항 삭제 실패');
      }
    } catch (err: any) {
      console.error('[deleteAnnouncement] Error:', err);
      const message = err.message || '공지사항 삭제 중 오류가 발생했습니다.';
      setError(message);
      throw new Error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    isSubmitting,
    error,
  };
}

// ============================================
// 첨부파일 업로드 Hook
// ============================================

export interface UploadProgress {
  fileId: string;
  fileName: string;
  progress: number; // 0-100
  status: 'pending' | 'uploading' | 'confirming' | 'done' | 'error';
  error?: string;
  attachment?: AnnouncementAttachment;
}

export interface UploadFileOptions {
  /**
   * V279.2: 진행 상태(uploadProgress)에 항목 추가 여부.
   * 본문 인라인 이미지 업로드는 첨부파일 진행 UI를 더럽히면 안 되므로 false 로 호출.
   * 기본값 true (기존 첨부파일 동작 유지).
   */
  trackProgress?: boolean;
}

export interface UseAnnouncementUploadResult {
  uploadFile: (file: File, options?: UploadFileOptions) => Promise<AnnouncementAttachment>;
  uploadFiles: (files: File[]) => Promise<AnnouncementAttachment[]>;
  uploadProgress: UploadProgress[];
  isUploading: boolean;
  clearProgress: () => void;
}

export function useAnnouncementUpload(): UseAnnouncementUploadResult {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * V267: Base64 callable 업로드 (signed URL 3단계 → 1단계로 대체)
   *
   * 기존 플로우: prepareAnnouncementUpload → fetch(signedUrl) → confirmAnnouncementUpload
   * 변경 플로우: uploadAnnouncementFile (Base64로 직접 전송, Admin SDK 업로드)
   *
   * V279.2: trackProgress=false 로 호출하면 uploadProgress 상태를 건드리지 않음.
   * 본문 인라인 이미지 업로드 전용 경로.
   */
  const uploadFile = async (
    file: File,
    options: UploadFileOptions = {}
  ): Promise<AnnouncementAttachment> => {
    const trackProgress = options.trackProgress !== false;
    if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

    // 파일 크기 제한 (10MB) — Base64 전송 특성상 제한
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('파일 크기는 10MB 이하만 업로드 가능합니다.');
    }

    const fileId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // V279.2: trackProgress 가 true 일 때만 진행 상태에 추가
    if (trackProgress) {
      setUploadProgress((prev) => [
        ...prev,
        { fileId, fileName: file.name, progress: 0, status: 'pending' },
      ]);
    }

    try {
      // 1. 파일을 Base64로 변환
      if (trackProgress) {
        setUploadProgress((prev) =>
          prev.map((p) => (p.fileId === fileId ? { ...p, status: 'uploading', progress: 10 } : p))
        );
      }

      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('파일 읽기 실패'));
        reader.readAsDataURL(file);
      });

      if (trackProgress) {
        setUploadProgress((prev) =>
          prev.map((p) => (p.fileId === fileId ? { ...p, progress: 30 } : p))
        );
      }

      // 2. callable로 직접 업로드 (Admin SDK가 Storage Rules 우회)
      const upload = httpsCallable<
        { fileName: string; contentType: string; fileBase64: string },
        UploadFileResponse
      >(functions, 'uploadAnnouncementFile');

      const result = await upload({
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileBase64,
      });

      if (!result.data.success) {
        throw new Error('파일 업로드 실패');
      }

      // 3. 완료
      if (trackProgress) {
        setUploadProgress((prev) =>
          prev.map((p) =>
            p.fileId === fileId
              ? { ...p, status: 'done', progress: 100, attachment: result.data.attachment }
              : p
          )
        );
      }

      return result.data.attachment;
    } catch (err: any) {
      console.error('[uploadFile] Error:', err);
      if (trackProgress) {
        setUploadProgress((prev) =>
          prev.map((p) =>
            p.fileId === fileId ? { ...p, status: 'error', error: err.message } : p
          )
        );
      }
      throw err;
    }
  };

  const uploadFiles = async (files: File[]): Promise<AnnouncementAttachment[]> => {
    setIsUploading(true);
    const results: AnnouncementAttachment[] = [];

    try {
      for (const file of files) {
        const attachment = await uploadFile(file);
        results.push(attachment);
      }
      return results;
    } finally {
      setIsUploading(false);
    }
  };

  const clearProgress = () => {
    setUploadProgress([]);
  };

  return {
    uploadFile,
    uploadFiles,
    uploadProgress,
    isUploading,
    clearProgress,
  };
}

// ============================================
// 권한 확인 Hook
// ============================================

export interface UseAnnouncementPermissionResult {
  canWrite: boolean;
  isLoading: boolean;
}

export function useAnnouncementPermission(): UseAnnouncementPermissionResult {
  // V86: 캐시에서 초기값 로드
  const [canWrite, setCanWrite] = useState(() => permissionCache?.canWrite || false);
  const [isLoading, setIsLoading] = useState(() => !permissionCache);

  // V86: 중복 호출 방지
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // V86: 이미 체크했거나 캐시가 유효하면 스킵
    if (hasFetchedRef.current) return;
    if (permissionCache && Date.now() - permissionCache.timestamp < CACHE_TTL) {
      setCanWrite(permissionCache.canWrite);
      setIsLoading(false);
      return;
    }

    const checkPermission = async () => {
      if (!functions) {
        setCanWrite(false);
        setIsLoading(false);
        return;
      }

      hasFetchedRef.current = true;

      try {
        const check = httpsCallable<void, CheckPermissionResponse>(
          functions,
          'checkAnnouncementPermission'
        );

        const result = await check();
        setCanWrite(result.data.canWrite);

        // V86: 캐시 업데이트
        permissionCache = {
          canWrite: result.data.canWrite,
          timestamp: Date.now(),
        };
      } catch (err) {
        console.error('[checkAnnouncementPermission] Error:', err);
        setCanWrite(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPermission();
  }, []);

  return { canWrite, isLoading };
}

// ============================================
// 역할 선택 옵션
// ============================================

/** 열람 대상 역할 옵션 */
export const VISIBILITY_ROLE_OPTIONS = [
  { id: 'all_employees', name: '전체 직원' },
  { id: 'role_hr', name: '인사팀 담당자' },
  { id: 'executive', name: '임원' },
  { id: 'system_admin', name: '시스템 관리자' },
  { id: 'role_team_leader', name: '팀장' },
  { id: 'role_exec', name: '경영진' },
];

// ============================================
// V84: 읽지 않은 공지사항 알림 Hook
// ============================================

interface GetUnreadCountResponse {
  success: boolean;
  unreadCount: number;
}

interface MarkSeenResponse {
  success: boolean;
  lastSeenAt: string;
}

export interface UseUnreadAnnouncementCountResult {
  unreadCount: number;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

/**
 * 읽지 않은 공지사항 개수 조회 Hook
 * Sidebar에서 배지 표시에 사용
 */
export function useUnreadAnnouncementCount(): UseUnreadAnnouncementCountResult {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // V70G8C: 공지 상세에서 읽음 처리되면 서버가 돌려준 최신 개수로 즉시 갱신 (새로고침 불필요)
  useEffect(() => subscribeUnreadAnnouncementCount((count) => {
    setUnreadCount(count);
    setIsLoading(false);
  }), []);

  const fetchCount = useCallback(async () => {
    if (!functions) {
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    try {
      const getCount = httpsCallable<void, GetUnreadCountResponse>(
        functions,
        'getUnreadAnnouncementCount'
      );

      const result = await getCount();
      if (result.data.success) {
        setUnreadCount(result.data.unreadCount);
      }
    } catch (err) {
      console.error('[useUnreadAnnouncementCount] Error:', err);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return {
    unreadCount,
    isLoading,
    refetch: fetchCount,
  };
}

// ============================================
// V70G8C: 공지 1건 읽음 처리 + LNB 배지 즉시 동기화
// ============================================

type UnreadListener = (count: number) => void;
const unreadListeners = new Set<UnreadListener>();

/** 미확인 개수 변경 구독 (Sidebar 배지) */
export function subscribeUnreadAnnouncementCount(listener: UnreadListener): () => void {
  unreadListeners.add(listener);
  return () => {
    unreadListeners.delete(listener);
  };
}

export function publishUnreadAnnouncementCount(count: number): void {
  unreadListeners.forEach((l) => l(count));
}

// ============================================
// V70G8E: 공지 1건 읽음 → 목록 레드닷 즉시 동기화
// ============================================

type ReadListener = (announcementId: string) => void;
const readListeners = new Set<ReadListener>();

/** 읽음 저장 성공 알림 구독 (공지 목록 화면) */
export function subscribeAnnouncementRead(listener: ReadListener): () => void {
  readListeners.add(listener);
  return () => {
    readListeners.delete(listener);
  };
}

/** 해당 공지만 읽음으로 바꾼 새 배열 (변경 없으면 같은 배열 반환) */
export function markItemRead(items: AnnouncementListItem[], announcementId: string): AnnouncementListItem[] {
  if (!items.some((i) => i.id === announcementId && i.isUnread)) return items;
  return items.map((i) => (i.id === announcementId && i.isUnread ? { ...i, isUnread: false } : i));
}

/** 현재 계정의 목록 캐시(모든 게시판)에서 해당 공지만 읽음 처리 — 목록 복귀 시 재조회 없이 반영 */
function markReadInListCache(announcementId: string): void {
  const uid = currentUid();
  listCacheByBoard.forEach((entry, board) => {
    if (entry.uid !== uid) return;
    listCacheByBoard.set(board, {
      ...entry,
      announcements: markItemRead(entry.announcements, announcementId),
      pinnedAnnouncements: entry.pinnedAnnouncements && markItemRead(entry.pinnedAnnouncements, announcementId),
      regularAnnouncements: entry.regularAnnouncements && markItemRead(entry.regularAnnouncements, announcementId),
    });
  });
}

/** 서버 읽음 저장 성공 후에만 호출 */
export function publishAnnouncementRead(announcementId: string): void {
  markReadInListCache(announcementId);
  readListeners.forEach((l) => l(announcementId));
}

/** 테스트 전용: 목록 캐시 초기화 */
export function resetAnnouncementListCacheForTest(): void {
  listCacheByBoard.clear();
}

/**
 * 공지 상세 화면에서 호출. 서버 저장 성공 시에만 배지를 서버 값으로 갱신한다.
 * 실패 시 배지는 그대로 두고(낙관적 차감 없음) null 반환.
 */
export async function markAnnouncementRead(announcementId: string): Promise<number | null> {
  if (!functions) return null;
  try {
    const fn = httpsCallable<
      { announcementId: string },
      { success: boolean; alreadyRead: boolean; unreadCount: number }
    >(functions, 'markAnnouncementRead');
    const res = await fn({ announcementId });
    if (res.data?.success && typeof res.data.unreadCount === 'number') {
      publishUnreadAnnouncementCount(res.data.unreadCount);
      // V70G8E: 저장 성공 시에만 목록 레드닷도 제거 (실패 시 그대로)
      publishAnnouncementRead(announcementId);
      return res.data.unreadCount;
    }
    return null;
  } catch (err) {
    console.warn('[markAnnouncementRead] 읽음 처리 실패 (배지 유지)', err);
    return null;
  }
}

/**
 * @deprecated V70G8C — 목록 진입만으로 전체 읽음 처리하던 방식. 서버에서도 no-op.
 *   읽음은 markAnnouncementRead 사용.
 */
export async function markAnnouncementsSeen(): Promise<void> {
  if (!functions) {
    return;
  }

  try {
    const markSeen = httpsCallable<void, MarkSeenResponse>(
      functions,
      'markAnnouncementsSeen'
    );

    await markSeen();
  } catch (err) {
    console.error('[markAnnouncementsSeen] Error:', err);
  }
}
